/*
 * <BeMenuBar> -- the .be-menu-bar row menu. Its own block rather than a modifier
 * on <BeMenu> because the item pad differs: GetItemMargins reports 8/2/8/2
 * against a column's standard_pad 14/1/20/1. All four of SetBorder's modes draw
 * (menu-bar.scss). Opening a menu from an item is not wired -- items are presentational.
 */

import type { ReactNode } from "react";

import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

/** BMenuBar::SetBorder. */
export type BeMenuBarBorder =
	/** `B_BORDER_FRAME` — one frame around the bar. R5's default. */
	| "frame"
	/** `B_BORDER_CONTENTS` — the same frame, struck heavier. */
	| "contents"
	/** `B_BORDER_EACH_ITEM` — the frame leaves the bar and rings every item. */
	| "each-item"
	/** `B_BORDER_OFF` — none. */
	| "off";

export type BeMenuBarProps = BeProps<
	"div",
	{
		border?: BeMenuBarBorder;

		/** Bar items. */
		children?: ReactNode;
	}
>;

export const BeMenuBar = beComponent<BeMenuBarProps, HTMLDivElement>("BeMenuBar", (props, be) => {
	const { border, scale, className, style, children, ...rest } = props;

	return (
		<div role="menubar" {...rest} {...be("be-menu-bar", border && border !== "frame" && `be-menu-bar--${border}`)}>
			{children}
		</div>
	);
});

export type BeMenuBarItemProps = BeProps<
	"button",
	{
		/** Force the selected appearance, as when its menu is open. */
		selected?: boolean;

		/** A 16x16 mini icon, the same slot a column menu's item uses. */
		icon?: ReactNode;

		/** Invoked on click; wired to the underlying button. */
		onSelect?: () => void;

		/** Item label. */
		children?: ReactNode;
	}
>;

export const BeMenuBarItem = beComponent<BeMenuBarItemProps, HTMLButtonElement>("BeMenuBarItem", (props, be) => {
	const { disabled, selected, icon, onSelect, scale, className, style, children, type: _type, ...rest } = props;

	return (
		<button
			type="button"
			role="menuitem"
			aria-haspopup
			aria-expanded={selected}
			disabled={disabled}
			onClick={onSelect}
			{...rest}
			{...be("be-menu-bar__item", icon != null && "be-menu-bar__item--icon", selected && "is-selected")}
		>
			{icon != null && <span className="be-menu-icon">{icon}</span>}
			{pixelText(children, "be-menu-bar__label")}
		</button>
	);
});
